/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import initBem from 'js/lib/bem';
import { FormattedMessage } from 'js/lib/coursera.react-intl';
import user from 'js/lib/user';

import { Button, InlineNotification, TextField, Typography2 } from '@coursera/cds-core';

import RichTextView from 'bundles/assess-common/components/RichTextView';
import type { AssignmentRole } from 'bundles/assignment-common/types/AssignmentRoles';
import { AssignmentRoles } from 'bundles/assignment-common/types/AssignmentRoles';
import { EXTRA_CREDIT_RUBRIC_POINTS_MULTIPLIER } from 'bundles/author-assignment-grading/components/common/utils/utils';
import CMLEditor from 'bundles/authoring/content-authoring/components/CMLEditor';
import type { CmlContent } from 'bundles/cml';
import { CMLOrHTML, CMLUtils } from 'bundles/cml';
import AiGradedRubricCoachFeedback from 'bundles/compound-assessments/components/form-parts/AiGradedRubricCoachFeedback';
import ValidationError from 'bundles/compound-assessments/components/form-parts/ValidationError';
import type { FormPartsValidationStatus } from 'bundles/compound-assessments/components/form-parts/lib/checkResponsesInvalid';
import { FormPartsValidationStatuses } from 'bundles/compound-assessments/components/form-parts/lib/constants';
import { parseValues } from 'bundles/compound-assessments/components/form-parts/multiLineInput/util';
import ProfileImageCA from 'bundles/compound-assessments/components/shared/ProfileImageCA';
import { GraderTypes, typeNames } from 'bundles/compound-assessments/constants';
import type {
  MultiLinePrompt,
  MultiLineResponse,
  ResponseMetadata,
} from 'bundles/compound-assessments/types/FormParts';
// FIXME: existing import/no-cycle violations are excused to prevent seeing errors when modifying other parts of the same file; please fix it carefully
// eslint-disable-next-line import/no-cycle
import { CmlEditorWithSuggestions } from 'bundles/grading';
import * as gradingCmlUtils from 'bundles/grading-common/utils/cmlUtils';

import _t from 'i18n!nls/compound-assessments';

const bem = initBem('MultiLineInput');

const styles = {
  container: css`
    border: 1px solid var(--cds-color-neutral-stroke-primary-weak);
    padding: var(--cds-spacing-200);
  `,
  invalidContainer: css`
    border-color: var(--cds-color-feedback-error);
  `,
  formParts: css`
    margin-top: var(--cds-spacing-200);

    h3 {
      margin-bottom: var(--cds-spacing-200);

      > em {
        color: var(--cds-color-neutral-primary-weak);
      }
    }
  `,
  textInput: css`
    display: flex;
    align-items: end;
    margin-bottom: var(--cds-spacing-300);
  `,
  textInputNote: css`
    white-space: nowrap;
    padding: 0 0 var(--cds-spacing-150) var(--cds-spacing-100);
  `,
  review: css`
    margin-bottom: 27px;
    padding-left: 41px;
    position: relative;

    &:last-child {
      margin-bottom: 0;
    }
  `,
  reviewAuthor: css`
    margin-bottom: var(--cds-spacing-100);
  `,
  reviewProfileImage: css`
    position: absolute;
    left: 0;
    top: -4px;
  `,
  aiFeedback: css`
    margin-top: var(--cds-spacing-200);
  `,
  aiScore: css`
    display: flex;
    gap: var(--cds-spacing-50);
    margin-bottom: var(--cds-spacing-200);
  `,
  aiScoreTitle: css`
    margin-top: 2px;
  `,
};

type Responses = Array<{ response: MultiLineResponse; metadata: ResponseMetadata }>;

export type Props = {
  prompt: MultiLinePrompt;
  response?: MultiLineResponse;
  responses?: Responses;
  metadata: ResponseMetadata;
  onChangeResponse: (response: MultiLineResponse) => void;
  isDisabled: boolean;
  isReadOnly: boolean;
  showValidation: boolean;
  role: AssignmentRole;
  courseId: string;
  autoFocus?: boolean;
  isQuickGraderContext?: boolean;
};

export const checkInvalid = (
  response: MultiLineResponse | null | undefined,
  prompt: MultiLinePrompt
): FormPartsValidationStatus | null => {
  const { input, score, richTextInput } = response?.definition?.reviewPart?.definition || {};
  const hasReviewPoints = !!prompt?.reviewPartSchemaDetails?.definition?.points;
  if (hasReviewPoints && typeof score !== 'number') {
    // score is required for review
    return FormPartsValidationStatuses.error;
  } else if (!input && !richTextInput) {
    return FormPartsValidationStatuses.warning;
  }
  return null;
};

type State = {
  scoreInputValue?: string;
};

const NUMBER_OR_FLOAT_REGEX = /^\d*(\.\d{0,2})?$/;

class MultiLineInput extends React.Component<Props, State> {
  state: State = {
    scoreInputValue: undefined,
  };

  extraCreditEnabled() {
    const { role } = this.props;
    return role === AssignmentRoles.GRADER;
  }

  onChangeScore = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { onChangeResponse, response, prompt } = this.props;
    const { value } = event.target as HTMLInputElement;
    const { input, richTextInput } = response?.definition?.reviewPart?.definition || {};

    if (String(value).match(NUMBER_OR_FLOAT_REGEX)) {
      this.setState({ scoreInputValue: value });
      const parsedValue = parseFloat(value);

      const { points } = prompt?.reviewPartSchemaDetails?.definition || {};
      const maxReviewPoints = this.extraCreditEnabled() ? points * EXTRA_CREDIT_RUBRIC_POINTS_MULTIPLIER : points;

      const score = Number.isNaN(parsedValue) ? undefined : Math.max(Math.min(parsedValue, maxReviewPoints), 0);

      onChangeResponse({
        typeName: typeNames.REVIEW_RESPONSE,
        definition: {
          aiSuggestionId: response?.definition.aiSuggestionId,
          graderType: GraderTypes.Human,
          reviewPart: {
            typeName: 'multiLineInput',
            definition: {
              richTextInput,
              input,
              score,
            },
          },
        },
      });
    }
  };

  onChangeRichTextInput = (cml: CmlContent) => {
    const { onChangeResponse, response } = this.props;
    const { score } = response?.definition?.reviewPart?.definition || {};

    onChangeResponse({
      typeName: typeNames.REVIEW_RESPONSE,
      definition: {
        aiSuggestionId: response?.definition.aiSuggestionId,
        graderType: GraderTypes.Human,
        reviewPart: {
          typeName: 'multiLineInput',
          definition: {
            input: undefined,
            richTextInput: cml,
            score,
          },
        },
      },
    });
  };

  render() {
    const { response, responses, prompt, isDisabled, isReadOnly, showValidation, autoFocus, isQuickGraderContext } =
      this.props;
    const { score } = response?.definition?.reviewPart?.definition || {};
    const formPartValue = parseValues(response?.definition?.reviewPart?.definition);

    let { scoreInputValue } = this.state;
    const reviewPoints: number | undefined = prompt?.reviewPartSchemaDetails?.definition?.points;

    if (!scoreInputValue || parseFloat(scoreInputValue) !== score) {
      scoreInputValue = typeof score === 'number' ? String(score) : '';
    }

    if (!prompt) {
      return null;
    }

    const combinedResponses: Responses = responses || [];
    // In read only mode we want to show either peer responses from `responses` field or your own
    // response from `response`. Collect everything in `responses` variable to render it using the
    // same code.
    if (isReadOnly) {
      let { metadata } = this.props;
      if (response) {
        if (!metadata) {
          const currentUser = user.get();
          metadata = {
            submitterProfile: {
              typeName: 'user',
              definition: {
                fullName: currentUser.full_name,
                photoUrl: currentUser.photo_120,
              },
            },
          };
        }
        combinedResponses.push({ response, metadata });
      }
    }

    const isInvalid = showValidation && checkInvalid(response, prompt);

    const hasScorePart = typeof reviewPoints === 'number' && reviewPoints >= 0;

    const isAiGraded = prompt.graderType === GraderTypes.Ai;
    const hasAiFeedback = formPartValue.richTextInput && !CMLUtils.isEmpty(formPartValue.richTextInput);

    return (
      <div className={bem()} css={[styles.container, isInvalid && styles.invalidContainer]}>
        <Typography2 component="div" variant="bodyPrimary">
          <CMLOrHTML value={prompt.promptContent} />
        </Typography2>
        {isReadOnly && !isAiGraded && (
          <div>
            {combinedResponses &&
              combinedResponses.map(({ response: { definition } = {}, metadata: { submitterProfile } = {} }, index) => {
                const { score: responseScore } = definition?.reviewPart?.definition || {};
                const responseValue = parseValues(definition?.reviewPart?.definition);

                return (
                  // eslint-disable-next-line react/no-array-index-key
                  <div className={bem('review')} css={styles.review} key={`response-${index}`}>
                    {submitterProfile && (
                      <div className={bem('review-author')} css={styles.reviewAuthor}>
                        <div className={bem('review-profile-image')} css={styles.reviewProfileImage}>
                          <ProfileImageCA profile={submitterProfile.definition} />
                        </div>
                        <Typography2 component="h4" variant="subtitleMedium">
                          {submitterProfile.definition.fullName}
                        </Typography2>
                      </div>
                    )}
                    {typeof reviewPoints === 'number' && (
                      <Typography2 component="p" variant="bodyPrimary">
                        <FormattedMessage
                          message={_t('Score {score}')}
                          score={
                            <Typography2 variant="subtitleMedium" component="span">
                              {responseScore}/{reviewPoints}
                            </Typography2>
                          }
                        />
                      </Typography2>
                    )}
                    <RichTextView submissionPart={{ richText: responseValue.richTextInput }} />
                  </div>
                );
              })}
          </div>
        )}

        {!isReadOnly && !isAiGraded && (
          <div className={bem('form-parts')} css={styles.formParts}>
            {hasScorePart && (
              <div className={bem('text-input')} css={styles.textInput}>
                <TextField
                  key={`text-input-${prompt.id}`}
                  disabled={isDisabled}
                  label={_t('Score')}
                  value={scoreInputValue}
                  onChange={this.onChangeScore}
                  {...(autoFocus && { autoFocus: true })}
                />
                <Typography2
                  className={bem('text-input-note')}
                  css={styles.textInputNote}
                  component="span"
                  variant="bodyPrimary"
                >
                  <FormattedMessage
                    message={_t(' out of {reviewPoints, plural, one {# point} other {# points}}')}
                    reviewPoints={reviewPoints}
                  />
                  {!!score && score > reviewPoints && (
                    <FormattedMessage
                      message={_t(' ({extraPoints, plural, one {# extra point} other {# extra points}})')}
                      extraPoints={score - reviewPoints}
                    />
                  )}
                </Typography2>
              </div>
            )}

            <Typography2 component="h3" variant="subtitleMedium">
              {_t('Comment')}
              {hasScorePart && (
                <Typography2 variant="subtitleMedium" component="em">
                  {' '}
                  {_t('(Optional)')}
                </Typography2>
              )}
            </Typography2>
            <div>
              {isQuickGraderContext ? (
                <CmlEditorWithSuggestions
                  key={`text-area-${prompt.id}`}
                  editorProps={{
                    initialCML: formPartValue.richTextInput,
                    isToolbarCompact: true,
                    onContentChange: this.onChangeRichTextInput,
                    customTools: gradingCmlUtils.EDITOR_TOOLS,
                    focusOnLoad: autoFocus && !hasScorePart,
                  }}
                />
              ) : (
                <CMLEditor
                  key={`text-area-${prompt.id}`}
                  initialCML={formPartValue.richTextInput}
                  isToolbarCompact
                  onContentChange={this.onChangeRichTextInput}
                  customTools={gradingCmlUtils.EDITOR_TOOLS}
                  focusOnLoad={autoFocus && !hasScorePart}
                />
              )}
            </div>
            {isInvalid && <ValidationError />}
          </div>
        )}

        {isAiGraded && (
          <div css={styles.aiFeedback}>
            {hasScorePart && (
              <div css={styles.aiScore}>
                <Typography2 css={styles.aiScoreTitle} variant="subtitleMedium" component="div">
                  {_t('Score:')}
                </Typography2>
                <Typography2 component="p" variant="bodyPrimary">
                  <FormattedMessage
                    message={_t('{score} out of {reviewPoints, plural, one {# point} other {# points}}')}
                    score={scoreInputValue}
                    reviewPoints={reviewPoints}
                  />
                  {!!score && score > reviewPoints && (
                    <FormattedMessage
                      message={_t(' ({extraPoints, plural, one {# extra point} other {# extra points}})')}
                      extraPoints={score - reviewPoints}
                    />
                  )}
                </Typography2>
              </div>
            )}
            {!hasAiFeedback && isAiGraded ? (
              <InlineNotification
                inlineAction={
                  <Button
                    size="small"
                    variant="ghost"
                    onClick={() => {
                      location.reload();
                    }}
                  >
                    {_t('Refresh')}
                  </Button>
                }
                severity="information"
                title={_t('Grades and feedback are not available yet')}
              >
                {_t('It can take a few minutes for the AI grading to complete. Please refresh and try again.')}
              </InlineNotification>
            ) : (
              <AiGradedRubricCoachFeedback feedback={formPartValue.richTextInput} />
            )}
          </div>
        )}
      </div>
    );
  }
}

export default MultiLineInput;
